import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as msal from 'msal';
import localforage from 'localforage';
import { isMobile, isAndroid, isIOS } from 'react-device-detect';
import {
  fetchMicrosoftLoginConfig,
  fetchEntraAccountByEmail
} from '../../api/v4';
import config from '../../config/config';
import {
  forgotPasswordRequest,
  signIn,
  submitSecurityQuestions,
  signOut,
  loginFailure
} from '../../actions/auth';
import { setActiveCompany } from '../../actions/company';
import {
  getLoginFailure,
  getSecurityQuestionsSelector
} from '../../selectors/auth';
import customToast from '../../utils/customToast';
import { usePrevious, useEffectUpdate } from '../../utils/hooks';
import { Button, Textbox } from '../../components/inputs';
import Chevron from '../../svgs/chevron';
import LoginForm from '../../forms/LoginForm';
import logoImage from '../TopHeader/logo.png';
import Modal from '../../components/Modal';
import ResetPasswordForm from '../../forms/ResetPasswordForm';
import { ResetUserPasswordForm } from '../../forms/ResetUserPasswordForm';

import styles from './login.module.scss';

export let SSOClient = null;

export default function Login(props) {
  const securityQuestions = useSelector(getSecurityQuestionsSelector);
  const loginFail = useSelector(getLoginFailure);

  const dispatch = useDispatch();
  const logoutUser = () => dispatch(signOut());
  const signInDispatcher = payload => dispatch(signIn(payload));
  const forgotPassword = email => dispatch(forgotPasswordRequest(email));
  const submitQuestions = payload => dispatch(submitSecurityQuestions(payload));
  const setLoginFailure = payload => dispatch(loginFailure(payload));
  const setCompany = useCallback(payload => {
    dispatch(setActiveCompany(payload));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [badUsername, setBadUsername] = useState(false);
  const [badPassword, setBadPassword] = useState(false);
  const [rememberUser, setRememberUser] = useState(false);
  const [mobileModal, setMobileModal] = useState(isMobile);
  const [loginPage, setLoginPage] = useState('login');
  const [isLoading, setIsLoading] = useState(false);
  const [entraEmailPrompt, setEntraEmailPrompt] = useState(false);
  const [entraEmail, setEntraEmail] = useState('');

  const prev = usePrevious({ isLoading, loginFail });

  const whitelabel = JSON.parse(localStorage.getItem('whitelabel'));
  const customLogo = whitelabel?.logo;
  const backdropEnabled = whitelabel?.backdropEnabled;

  useEffect(() => {
    localforage.clear();
    sessionStorage.clear();
    setCompany({});
    if (localStorage.getItem('username')) setRememberUser(true);
  }, [setCompany]);

  useEffectUpdate(() => {
    if (prev?.isLoading) setIsLoading(false);

    if (prev.loginFail === 'Your username or password is incorrect') {
      setBadPassword(true);
      setBadUsername(true);
      setLoginFailure('');
    }
  }, [prev?.isLoading, prev?.loginFailure]);

  const handleChangeCheckbox = checked => {
    if (!checked) {
      localStorage.removeItem('username');
    }
    setRememberUser(checked);
  };

  const initiateEntraLogin = async email => {
    try {
      const result = await fetchEntraAccountByEmail(email);
      if (result?.accountId) {
        localStorage.setItem('entraEmail', email);
        window.location.href = `${config.apiUrl}/v4/useraccounts/entra/authorize?accountId=${result.accountId}`;
      }
    } catch (e) {
      localStorage.removeItem('entraEmail');
      customToast('No Entra configuration found for this email.', 'error');
    }
  };

  const handleEntraSubmit = () => {
    const cachedEmail = localStorage.getItem('entraEmail');
    if (cachedEmail) {
      initiateEntraLogin(cachedEmail);
    } else {
      setEntraEmailPrompt(true);
    }
  };

  const handleEntraEmailSubmit = () => {
    if (!entraEmail) {
      customToast('Please enter your work email address.', 'error');
      return;
    }
    setEntraEmailPrompt(false);
    initiateEntraLogin(entraEmail);
  };

  const handleLoginSubmit = values => {
    if (!values.username) {
      if (!values.password) {
        setBadUsername(true);
        setBadPassword(true);
        customToast('Missing Username and Password.', 'error');
      } else {
        setBadUsername(true);
        setBadPassword(false);
        customToast('Missing Username.', 'error');
      }
    } else if (!values.password) {
      setBadUsername(false);
      setBadPassword(true);
      customToast('Missing Password.', 'error');
    } else {
      signInDispatcher({
        ...values,
        referrer: props?.location?.state?.referrer
      });
      setIsLoading(true);
      if (rememberUser) {
        localStorage.setItem('username', values.username);
      }
    }
  };
  return (
    <>
      {loginPage === 'login' ? (
        <div className={styles.loginWholePage}>
          {customLogo && (
            <div className={styles.poweredBy}>
              <img
                src={require('../../assets/images/iReport_grey_green.png')}
                alt="Powered by iReport"
                className={styles.poweredByLogo}
              />
            </div>
          )}
          <div className={styles.loginFlexbox}>
            <div className={styles.login}>
              {customLogo ? (
                <div className={styles.customLogoContainer}>
                  <img
                    src={customLogo}
                    className={styles.customLogo}
                    style={{
                      backgroundColor: backdropEnabled
                        ? 'var(--color-green)'
                        : '#fff'
                    }}
                    alt="customLogo"
                  />
                </div>
              ) : (
                <img src={require('../../assets/images/Logo.png')} alt="" />
              )}

              <LoginForm
                onSubmit={handleLoginSubmit}
                handleChangeCheckbox={handleChangeCheckbox}
                rememberUser={rememberUser}
                isLoading={isLoading}
                badUsername={badUsername}
                badPassword={badPassword}
                setLoginPage={value => setLoginPage(value)}
                onEntraSubmit={handleEntraSubmit}
                onMicrosoftSubmit={async event => {
                  const config = await fetchMicrosoftLoginConfig();
                  if (config) {
                    const msalConfig = {
                      auth: config
                    };
                    SSOClient = new msal.UserAgentApplication(msalConfig);
                    SSOClient.loginPopup()
                      .then(response => {
                        return (
                          signInDispatcher({
                            ...response,
                            referrer: props?.location?.state?.referrer,
                            isMicrosoft: true
                          }) && setIsLoading(true)
                        );
                      })
                      .catch(e => {
                        SSOClient = null;
                      });
                  }
                }}
              />
            </div>
          </div>
        </div>
      ) : (
        <div className={styles.passwordRecoveryWholePage}>
          <Button
            className={styles.passwordRecoveryBackButton}
            color="greenOutline"
            text="Back to Login"
            image={<Chevron className={styles.greenChevron} />}
            shrink
            onClick={() => {
              sessionStorage.clear();
              logoutUser();
              return setLoginPage('login');
            }}
          />
          <div className={styles.passwordRecovery}>
            {securityQuestions &&
            Array.isArray(securityQuestions) &&
            securityQuestions.length > 1 ? (
              <ResetUserPasswordForm
                securityQuestions={securityQuestions}
                submitQuestions={submitQuestions}
                logoutUser={logoutUser}
              />
            ) : (
              <ResetPasswordForm onSubmit={values => forgotPassword(values)} />
            )}
          </div>
        </div>
      )}
      <Modal
        isOpen={entraEmailPrompt}
        title="Sign in with Microsoft Entra"
        titleClassName="blueHeader"
        submitButtonColor="blue"
        submitButtonText="Continue"
        onRequestClose={() => setEntraEmailPrompt(false)}
        submitActions={handleEntraEmailSubmit}
      >
        <Textbox
          fieldLabel="Enter your work email address"
          currentValue={entraEmail}
          onChange={v => setEntraEmail(v)}
          placeholder="user@company.com"
          name="entraEmail"
        />
        {localStorage.getItem('entraEmail') && (
          <Button
            color="redOutline"
            text="Clear Saved Email"
            shrink
            onClick={() => {
              localStorage.removeItem('entraEmail');
              setEntraEmail('');
            }}
          />
        )}
      </Modal>
      <Modal
        isOpen={mobileModal}
        title="Best Viewed on our Mobile App"
        titleClassName="blueHeader"
        className={styles.modalMobile}
        hideButtons
        onRequestClose={() => setMobileModal(false)}
      >
        <img
          src={customLogo || logoImage}
          alt="iReportSource logo"
          className={styles.loginModalLogo}
        />
        <h4 className={styles.loginMessageHeader}>
          Simplify your safety wherever you go
        </h4>
        {isAndroid && (
          <a href="https://play.google.com/store/apps/details?id=com.ireportsource.ireport&pcampaignid=MKT-Other-global-all-co-prtnr-py-PartBadge-Mar2515-1">
            <img
              className={styles.loginAndroidBadge}
              alt="Get it on Google Play"
              src="https://play.google.com/intl/en_us/badges/images/generic/en_badge_web_generic.png"
            />
          </a>
        )}
        {isIOS && (
          <a href="https://itunes.apple.com/app/id1439251127">
            <img
              src="https://linkmaker.itunes.apple.com/en-us/badge-lrg.svg?releaseDate=2018-10-19&kind=iossoftware&bubble=ios_apps"
              alt="Get it from iTunes"
            />
          </a>
        )}
      </Modal>
    </>
  );
}
